/**
 * Sign-in with your mobile (design-handoff/components/sign-in-account.md, specs/signin-spec.md).
 *
 * The code is sent and checked by the shop's OTP app, Lucent – OTP Login & Notify (its app
 * embed is on in this theme): it is what logs a customer in, so its flow is kept as it is.
 * The design is the app's popup dressed as the design's sheet (phone) or 420 panel (600+):
 * assets/cw-signin.css restyles it, and this file puts in the design's words, the benefits
 * card and the field's hints when it opens. What the app's markup cannot be made to do (one
 * name field instead of first + last, "Get code on WhatsApp") is the customisation request
 * the handoff says to send to Lucent with these screens.
 *
 * - Logged out, the bar's "Login" (the Account tab / the header button) opens it, and so does
 *   the basket's LOG IN TO CHECKOUT (cw-delivery.js: asked at checkout, never before, owner 2 Oct;
 *   the words, owner 3 Oct). Order Again opens its own logged-out screen with a LOGIN button
 *   (cw-order-again.js). After the code, with "Logged in as {first name}": from "Login" on Home,
 *   to Categories; from "Login" anywhere else, the shopper stays where they were; Order Again
 *   comes back to Order Again; CHECKOUT goes straight on to checkout ("… Opening checkout…").
 *   The words are "Login" / "Log in" everywhere, the pair of the Account's LOG OUT (owner, 2 Oct).
 * - From CHECKOUT, when logging in does not work (the code never comes, or it is refused), the
 *   sheet offers "Continue without logging in": the visit is marked a guest's and checkout opens.
 *   A trouble at the OTP app must never stop an order.
 * - The app opens its popup on a tap on any link to /account; that is how it is asked here.
 *   If it is not on the page, the link goes to Shopify's own sign-in.
 */
// A customer who signed in with their phone may have no email: the id is what says so.
const signedIn = () => !!document.body.dataset.customer;
const INTENT_KEY = 'cw:signin:then';
// Where the shopper goes after signing in; and which of those ask for it themselves.
const WANTS = ['account', 'orderagain', 'checkout'];
const ASKS_FIRST = ['account'];
// 'login': the bar's Login; 'login-toast': already moved on (Home -> Categories), only the toast.
const GUEST_KEY = 'cw:checkout:guest';

const BENEFITS = ['Track your orders', 'Order Again in a tap', 'Your saved addresses'];
const VALID = /^[6-9]\d{9}$/;
/** Ten digits: a +91 / 91 in front or a leading 0 is dropped. */
function tenDigits(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length > 10 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}
const TICK =
  '<svg viewBox="0 0 256 256" width="11" height="11" aria-hidden="true"><path fill="currentColor" d="m232.49 80.49l-128 128a12 12 0 0 1-17 0l-56-56a12 12 0 1 1 17-17L96 183L215.51 63.51a12 12 0 0 1 17 17Z"/></svg>';

function remember(then) {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify({ then, at: Date.now() }));
  } catch {
    // the shopper still signs in; they just are not taken back to the panel
  }
}

/** The pending reason to sign in, without using it up. */
function peek() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(INTENT_KEY) || 'null');
    return raw && Date.now() - raw.at < 15 * 60 * 1000 ? raw.then : null;
  } catch {
    return null;
  }
}

function recall() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(INTENT_KEY) || 'null');
    sessionStorage.removeItem(INTENT_KEY);
    return raw && Date.now() - raw.at < 15 * 60 * 1000 ? raw.then : null;
  } catch {
    return null;
  }
}

/*
 * Our sheets are modal <dialog>s (assets/cw-sheet.js): the browser draws an open one above
 * everything else on the page and makes the rest untouchable, and the app's popup is part of
 * the rest (the app closes Horizon's own account dialog for the same reason). So when the popup
 * opens over a sheet (the basket's CHECKOUT, Order Again's LOGIN at 600+), the sheet is put
 * away in the same moment and the login takes its place over the same dimmed page. Closed
 * without logging in, the sheet comes back as it was. A login that works never closes the
 * popup: the app reloads the page it was opened on, and `then` carries on there.
 */
let parked = [];

function park() {
  for (const dialog of document.querySelectorAll('dialog[open]')) {
    const sheet = dialog.parentElement;
    if (!dialog.matches(':modal') || typeof sheet?.closeNow !== 'function') continue;
    sheet.closeNow();
    parked.push(sheet);
  }
}

/** Closed without a login: back to the sheet it opened over, and the reason to log in goes. */
function unpark() {
  recall();
  const back = parked;
  parked = [];
  for (const sheet of back) sheet.open();
}

/** Open sign-in; `then` ('account' | 'orderagain' | 'checkout' | '' for none) follows it. */
export function signIn(then = '') {
  remember(then);
  let link = document.querySelector('a[data-cw-signin-link]');
  if (!link) {
    link = document.createElement('a');
    link.href = `${window.Shopify?.routes?.root || '/'}account`;
    link.dataset.cwSigninLink = '';
    link.tabIndex = -1;
    link.setAttribute('aria-hidden', 'true');
    link.className = 'cw-sr';
    document.body.appendChild(link);
  }
  link.click();
}

/* ---------------- The app's popup, in the design's words ---------------- */

const setText = (el, text) => {
  if (el && el.textContent.trim() !== text) el.textContent = text;
};

/*
 * The app's own settings, put right for this shop (specs/signin-spec.md). The app reads them
 * from the page (window.simplyOtp) at the moment they matter, and its admin has no switch for
 * them (6 Oct), so they are set here:
 * - Email is optional, as the design says. Required, it stopped every customer whose record has
 *   no email, which is most of the shop's phone customers: after the code the app opens its form
 *   and will not go on without one. Left empty now, the app fills in a stand-in itself
 *   (+91…@gmail.com, its own way for shops that do not ask for email) and the customer is in.
 *   Nobody reads that address and the shop never shows it (snippets/cw-own-email.liquid).
 * - A name is required, the design's "Your name (required)": it is what the form is for.
 * - The code can be asked for again after 0:30, as drawn; the app's 5 s invites a second tap
 *   before the first SMS has come.
 */
function tune() {
  const otp = window.simplyOtp;
  if (otp?.language) {
    otp.language.email_required = false;
    otp.language.fname_required = true;
  }
  if (otp?.settings) otp.settings.resendTime = Math.max(Number(otp.settings.resendTime) || 0, 30);
}

/*
 * A returning customer is not asked again (the design's "First time only"). The app opens its
 * form after the code whenever their record has no email, with their name already in it; with
 * email optional there is nothing left to ask, so the form is sent for them the moment it opens
 * and the sheet says "Logging you in…" meanwhile. If the app answers with an error, the form
 * comes back with it. A first-timer's form opens with no name in it, and stays theirs to fill.
 * The offers box opens unticked, each time the form opens (owner, 6 Oct: a customer says yes to
 * offers, it is never assumed; the app's own box comes ticked). A tick the customer gives stays.
 * A record that already has an email keeps it: the app opens its form with the email box empty
 * even then (it fills it only for an email sign-in), and an empty box would be sent as the
 * stand-in, over the customer's real address. The app keeps what it was told in visitorApiData.
 */
function returning(update) {
  // Only real changes: dress() runs on every change of a class in the popup, and a class
  // removed that was not there still counts as one (it would run again, for ever).
  if (update.classList.contains('hideBox')) {
    delete update.dataset.cwSeen;
    if (update.classList.contains('cw-auto')) update.classList.remove('cw-auto');
    return;
  }
  if (update.classList.contains('cw-auto') && update.querySelector('.toast-box-wrapper')) update.classList.remove('cw-auto');
  if (update.dataset.cwSeen) return;
  update.dataset.cwSeen = '1';
  const offers = update.querySelector('.marketing-option');
  if (offers) offers.checked = false;
  const known = window.simplyOtp?.visitorApiData?.email;
  const email = update.querySelector('.update-user-email-input');
  if (known && email && !email.value.trim()) {
    email.value = known;
    email.classList.add('is-filled');
  }
  if (!update.querySelector('.update-user-first-name-input')?.value.trim()) return;
  update.classList.add('cw-auto');
  update.querySelector('.update-btn')?.click();
}

function dress(wrap) {
  tune();
  const ol = wrap.querySelector('.olWrapper .ol') || wrap;

  // 1. Phone
  const login = ol.querySelector('.login-box');
  if (login) {
    setText(login.querySelector('.login-header'), 'Log in with your mobile');
    setText(
      login.querySelector('.login-description'),
      peek() === 'checkout'
        ? 'Log in to place this order. We’ll send a 6-digit code by SMS.'
        : 'We’ll send a 6-digit code by SMS. New here? This creates your account.'
    );
    const phone = login.querySelector('.mobile-no-inner input.user-name-input');
    if (phone && !phone.dataset.cwDressed) {
      phone.dataset.cwDressed = '1';
      phone.placeholder = '10-digit mobile number';
      phone.setAttribute('inputmode', 'numeric');
      phone.setAttribute('autocomplete', 'tel-national');
      phone.setAttribute('aria-label', 'Mobile number');
      // The app caps the field at 10 characters, which cuts a typed "+91 98450 12345" short;
      // the room lets the whole number in, and tidy() keeps the ten that matter.
      phone.setAttribute('maxlength', '16');
      // CONTINUE wakes up at 10 digits starting 6-9; a typed or pasted +91 is understood and
      // dropped before the app reads the field (on blur, and just before CONTINUE).
      const tidy = () => {
        const d = tenDigits(phone.value);
        if (VALID.test(d) && phone.value !== d) {
          phone.value = d;
          phone.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };
      phone.addEventListener('input', () => {
        login.classList.toggle('cw-ready', VALID.test(tenDigits(phone.value)));
        login.classList.toggle('cw-typing', phone.value.length > 0);
      });
      phone.addEventListener('blur', tidy);
      login.addEventListener('click', (event) => event.target.closest('.send-btn') && tidy(), true);
      login.addEventListener('keydown', (event) => event.key === 'Enter' && tidy(), true);
    }
    const box = login.querySelector('.login-inputBox');
    if (box && !login.querySelector('.cw-benefits')) {
      box.insertAdjacentHTML(
        'afterend',
        `<ul class="cw-benefits" role="list">${BENEFITS.map((b) => `<li><span class="cw-benefits__tick">${TICK}</span>${b}</li>`).join('')}</ul>`
      );
    }
    const send = login.querySelector('.send-btn span') || login.querySelector('.send-btn');
    setText(send, 'CONTINUE');
    const consent = login.querySelector('.sotp-consent-wrapper');
    if (consent && !consent.dataset.cwDressed) {
      consent.dataset.cwDressed = '1';
      const links = [...consent.querySelectorAll('a')];
      const privacy = links.find((a) => /privacy/i.test(a.textContent + a.href));
      const terms = links.find((a) => /terms|t&c/i.test(a.textContent + a.href));
      consent.innerHTML = `<p class="consent-text">By continuing, you agree to our <a class="consent-link" href="${terms?.href || '/policies/terms-of-service'}">Terms</a> and <a class="consent-link" href="${privacy?.href || '/policies/privacy-policy'}">Privacy Policy</a>.</p>`;
    }
  }

  // 2. Code
  const verify = ol.querySelector('.verify-box');
  if (verify) {
    setText(verify.querySelector('.login-header'), 'Enter the 6-digit code');
    const sub = verify.querySelector('.login-description');
    if (sub && sub.firstChild && sub.firstChild.nodeType === Node.TEXT_NODE && !sub.dataset.cwDressed) {
      sub.dataset.cwDressed = '1';
      sub.firstChild.textContent = 'Sent by SMS to ';
    }
    const edit = verify.querySelector('.edit-phone');
    if (edit && !edit.dataset.cwDressed) {
      edit.dataset.cwDressed = '1';
      edit.setAttribute('role', 'button');
      edit.setAttribute('tabindex', '0');
      edit.setAttribute('aria-label', 'Change number');
    }
    const boxes = [...verify.querySelectorAll('.otp-input-box')];
    // SMS autofill is the app's own hidden field (autocomplete="one-time-code").
    boxes.forEach((b, i) => b.setAttribute('aria-label', `Digit ${i + 1}`));
    setText(verify.querySelector('.verify-btn span') || verify.querySelector('.verify-btn'), 'VERIFY');
    setText(verify.querySelector('.resend-otp-text'), 'Didn’t get it?');
    setText(verify.querySelector('.resend-btn'), 'Resend SMS');
    guestWay(wrap, verify);
  }

  // 3. First time only
  const update = ol.querySelector('.update-user-box');
  if (update) {
    setText(update.querySelector('.login-header'), 'Welcome to Chakkiwale');
    setText(update.querySelector('.login-description'), 'What name should we use for your deliveries?');
    // The app asks first and last name separately (the design's single "Your name" is part of
    // the customisation request to Lucent); the first is required, the last is not (tune()).
    setText(update.querySelector('.input-label.first-name'), 'First name');
    setText(update.querySelector('.input-label.last-name'), 'Last name (optional)');
    setText(update.querySelector('.input-label.email'), 'Email (optional, for order receipts)');
    setText(update.querySelector('.error-fname-message'), 'Please enter your name');
    const first = update.querySelector('.update-user-first-name-input');
    if (first) first.placeholder = 'e.g. Priya';
    const last = update.querySelector('.update-user-last-name-input');
    if (last) last.placeholder = 'e.g. Sharma';
    setText(update.querySelector('.update-btn span') || update.querySelector('.update-btn'), 'SAVE AND CONTINUE');
    returning(update);
  }

  // An email typed here that is already on another customer's record: the app refuses it with
  // "Your Email Address is Already Registered…", a dead end. Email is optional, so say the way on.
  const refused = ol.querySelector('.toast-card.error .toast-title');
  if (refused && /already registered/i.test(refused.textContent)) {
    setText(refused, 'That email is already on another account. Leave it empty and tap SAVE AND CONTINUE.');
  }
}

/**
 * From CHECKOUT only: "Continue without signing in", shown once signing in is not working —
 * the shopper asked for the code again, or tapped VERIFY twice and is still here, or 40 seconds
 * passed on the code step. It closes the sheet, marks the visit a guest's, and checkout goes on.
 */
function guestWay(wrap, verify) {
  if (peek() !== 'checkout' || verify.querySelector('.cw-guest')) return;
  verify.insertAdjacentHTML(
    'beforeend',
    '<button type="button" class="cw-guest" hidden>Trouble logging in? <span>Continue without logging in</span></button>'
  );
  const link = verify.querySelector('.cw-guest');
  let verifies = 0;
  const show = () => {
    link.hidden = false;
  };
  verify.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('.resend-btn')) show();
      if (event.target.closest('.verify-btn') && ++verifies >= 2) show();
    },
    true
  );
  setTimeout(show, 40000);
  link.addEventListener('click', () => {
    try {
      sessionStorage.setItem(GUEST_KEY, String(Date.now()));
    } catch {
      // without storage, checkout still opens; the next CHECKOUT asks again
    }
    recall();
    // A guest's basket says plain CHECKOUT from now on (cw-basket.css).
    document.documentElement.classList.add('cw-as-guest');
    // On to checkout: the basket stays away.
    parked = [];
    wrap.querySelector('.sotp-popup-close-btn')?.click();
    document.dispatchEvent(new CustomEvent('cw:checkout:go'));
  });
}

function watch() {
  const wrap = document.querySelector('.sotp-popup-wrapper');
  if (!wrap || wrap.dataset.cwWatched) return !!wrap;
  wrap.dataset.cwWatched = '1';
  dress(wrap);
  let shown = wrap.classList.contains('active');
  new MutationObserver(() => {
    const active = wrap.classList.contains('active');
    if (active) dress(wrap);
    document.documentElement.classList.toggle('cw-signing-in', active);
    if (active && !shown) park();
    if (!active && shown) unpark();
    shown = active;
  }).observe(wrap, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
  // Esc closes it, as it closes every sheet (the app has no key for it).
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && wrap.classList.contains('active')) wrap.querySelector('.sotp-popup-close-btn')?.click();
  });
  // The "Change" link under the code, from the keyboard too.
  wrap.addEventListener('keydown', (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.closest?.('.edit-phone')) {
      event.preventDefault();
      event.target.closest('.edit-phone').click();
    }
  });
  return true;
}

// The app's settings are on the page before this runs (its embed sets them inline).
tune();

// The app's markup arrives with its embed; dress it as soon as it is there.
if (!watch()) {
  const look = new MutationObserver(() => {
    if (watch()) look.disconnect();
  });
  look.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => look.disconnect(), 15000);
}

/* ---------------- Who asked, and back there after ---------------- */

// Asked from elsewhere (CHECKOUT, Order Again's SIGN IN, search's REQUEST): { then } follows
// the code. Claimed, so an asker with a fallback (search: Shopify's own sign-in) does not use it.
document.addEventListener('cw:signin:open', (event) => {
  event.preventDefault();
  signIn(event.detail?.then || '');
});

// Signed out: these ask for sign-in first (capture, before any panel claims them).
for (const want of ASKS_FIRST) {
  document.addEventListener(
    `cw:${want}:open`,
    (event) => {
      if (signedIn()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // The bar's Login: after the code, Home goes on to Categories, any other page stays.
      signIn(want === 'account' ? 'login' : want);
    },
    true
  );
}

// Signed in now, and something asked before: say so, and open it.
if (signedIn()) {
  const then = recall();
  if (then !== null) {
    const first = String(document.body.dataset.customerName || '').trim().split(/\s+/)[0];
    window.addEventListener('load', () => {
      setTimeout(() => {
        if (then === 'login' && document.body.dataset.template === 'index') {
          // From Home, logged in: on to Categories, where the toast is said instead.
          const cats = document.querySelector('a[data-cw="categories"]')?.href;
          if (cats) {
            remember('login-toast');
            window.location.href = cats;
            return;
          }
        }
        let said = first ? `Logged in as ${first}` : '';
        if (then === 'checkout') {
          // Straight on to checkout (owner, 3 Oct); it takes a moment to come up, so the toast
          // says it is on its way. An address still missing its flat opens its form instead.
          const go = new CustomEvent('cw:checkout:go', { detail: {} });
          document.dispatchEvent(go);
          if (go.detail.opening) said = said ? `${said}. Opening checkout…` : 'Opening checkout…';
        } else if (WANTS.includes(then)) document.dispatchEvent(new CustomEvent(`cw:${then}:open`, { cancelable: true, detail: {} }));
        // After the panel opens, so the toast shows inside it rather than under it.
        if (said) document.dispatchEvent(new CustomEvent('cw:toast', { detail: { text: said } }));
      }, 300);
    });
  }
}
